import { useAppSelector } from '../../../../redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
  toggleTheme,
} from '../../../../features/setting/setting.slice.ts'
import { IdeyaSegmented } from '../../../antd/ideya-segmented.tsx'
import Select from 'antd/es/select'
import { useDispatch } from 'react-redux'
import LightModeOutlined from '@assets/icons/navigation/light-mode-outlined.svg'
import DarkModeOutlined from '@assets/icons/navigation/dark-mode-outlined.svg'
import DarkLightModeOutlined from '@assets/icons/navigation/dark-mode-light-mode-outlined.svg'
import DarkDarkModeOutlined from '@assets/icons/navigation/dark-mode-dark-mode-outlined.svg'
import {
  BulbOutlined,
  CalendarOutlined,
  LogoutOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  ToolOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons'
import IdeyaTypography from '../../../antd/ideya-typography.tsx'
import React, { JSX, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Divider from 'antd/es/divider'
import { Theme } from '../../../../features/setting/setting.def.ts'
import { useTranslation } from 'react-i18next'
import { selectUser } from '../../../../features/user/user.slice.ts'
import {
  FAQ_BASE_URL,
  isAuth0,
  isInternalWebsite,
  isIppon,
  isV2Ready,
} from '../../../../utils/helpers.ts'
import { useChangePasswordMutation } from '../../../../apis/user-management/user-management-api.api.ts'
import { useIsSuperViewer } from '../../../hooks/use-is-superviewer.ts'
import { NEW_RELIC, newRelic } from '../../../../utils/new-relic.ts'
import { logoutNoRedirect } from '../../../../utils/logout.ts'
import { useAuth0 } from '@auth0/auth0-react'
import { SHOW_IDEA_LIST_TUTORIAL_BANNER } from '../../../../utils/constants.ts'
import { IdeyaButton } from '../../../antd/ideya-button/ideya-button.tsx'

export const UserMenu = () => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const theme = useAppSelector(selectTheme)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const userInfo = useAppSelector(selectUser)
  const isInternalUser = userInfo.role !== ''
  const isSuperViewer = useIsSuperViewer()
  const { logout: auth0Logout } = useAuth0()

  const toggleThemeSwitch = () => {
    dispatch(toggleTheme())
  }

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language)
  }

  const currentLanguage = i18n.resolvedLanguage ?? i18n.language ?? 'en'
  const faqLanguage =
    currentLanguage === 'cn'
      ? 'zh-cn'
      : currentLanguage === 'tw'
        ? 'zh-tw'
        : currentLanguage

  const [changePassword, { data: changePasswordData, isLoading }] =
    useChangePasswordMutation()

  const handleChangePassword = () => {
    changePassword()
  }

  useEffect(() => {
    if (changePasswordData && changePasswordData.data) {
      if (isIppon || (isAuth0 && !isInternalWebsite)) {
        console.log(changePasswordData.data)
        auth0Logout({
          openUrl: false,
        })
          .then(() => {
            localStorage.removeItem('apiAccessToken')
            localStorage.removeItem('disclaimerAccepted')
            localStorage.removeItem('ideya-error')
          })
          .finally(() => {
            window.location.href = changePasswordData.data.url
          })
      } else {
        logoutNoRedirect()
        window.location.href = changePasswordData.data.url
      }
    }
  }, [changePasswordData, auth0Logout])

  const languageActions: Record<string, keyof typeof NEW_RELIC.PAGE_ACTION> = {
    en: 'changeLanguageEn',
    jp: 'changeLanguageJp',
    cn: 'changeLanguageCn',
    tw: 'changeLanguageZh',
  }

  return (
    <>
      <div
        style={{
          borderRadius: themeStyle.borderRadiusLG,
          backgroundColor: themeStyle.contentBg,
          width: '100%',
          minWidth: 200,
          maxWidth: 400,
          boxShadow:
            '0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 9px 28px 8px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: `${themeStyle.paddingBase} ${themeStyle.paddingContentHorizontalLG}`,
          gap: themeStyle.marginBase,
          touchAction: 'manipulation',
        }}
      >
        <IdeyaSegmented
          value={theme}
          onChange={toggleThemeSwitch}
          block
          options={[
            {
              value: Theme.Light,
              label: (
                <SegmentLabel>
                  {theme === Theme.Dark ? (
                    <DarkLightModeOutlined />
                  ) : (
                    <LightModeOutlined />
                  )}
                  <span>{t('user_menu.light')}</span>
                </SegmentLabel>
              ),
            },
            {
              value: Theme.Dark,
              label: (
                <SegmentLabel>
                  {theme === Theme.Dark ? (
                    <DarkDarkModeOutlined />
                  ) : (
                    <DarkModeOutlined />
                  )}
                  <span>{t('user_menu.dark')}</span>
                </SegmentLabel>
              ),
            },
          ]}
        />
        {isV2Ready && (
          <div>
            <IdeyaTypography.Text type={'secondary'} size={'sm'}>
              {t('user_menu.user')}
            </IdeyaTypography.Text>
            <Divider />
          </div>
        )}
        {isV2Ready && (
          <div>
            <MenuNavigationButton
              icon={<UserOutlined />}
              label={t('user_menu.profile')}
            />
          </div>
        )}
        {isV2Ready && (
          <div>
            <MenuNavigationButton
              icon={<TrophyOutlined />}
              label={t('user_menu.my_achievements')}
            />
          </div>
        )}
        <div>
          <IdeyaTypography.Text type={'secondary'} size={'sm'}>
            {t('user_menu.system')}
          </IdeyaTypography.Text>
          <Divider />
        </div>
        <div>
          <MenuNavigationButton
            icon={<BulbOutlined />}
            label={t('user_menu.tips_for_new_users')}
            onClick={() => {
              localStorage.setItem(SHOW_IDEA_LIST_TUTORIAL_BANNER, 'true')
              window.location.reload()
            }}
          />
        </div>
        {userInfo.region !== 'Global' && !isSuperViewer && (
          <div>
            <MenuNavigationButton
              icon={<ReadOutlined />}
              label={t('user_menu.rules')}
              onClick={() => navigate('/rules')}
            />
          </div>
        )}
        <div>
          <MenuNavigationButton
            icon={<QuestionCircleOutlined />}
            label={'FAQ'}
            onClick={() =>
              window.open(`${FAQ_BASE_URL}/${faqLanguage}`, '_blank')
            }
          />
        </div>
        <div>
          <MenuNavigationButton
            icon={<MessageOutlined />}
            label={t('user_menu.support')}
            onClick={() =>
              window.open('mailto:support@polymerideya.com', '_self')
            }
          />
        </div>
        <Select
          value={currentLanguage}
          style={{ width: '100%' }}
          onChange={(value) => {
            changeLanguage(value)
            const action = languageActions[value]
            if (action) newRelic.addPageAction(NEW_RELIC.PAGE_ACTION[action])
          }}
          options={[
            { value: 'en', label: 'English' },
            { value: 'jp', label: '日本語' },
            { value: 'cn', label: '中文 (简体)' },
            { value: 'tw', label: '中文 (繁體)' },
          ]}
        />
        <Divider />
        {isV2Ready && (
          <IdeyaButton icon={<CalendarOutlined />}>
            {t('user_menu.leave_dates_apply')}
          </IdeyaButton>
        )}
        {!isInternalUser && (
          <IdeyaButton
            icon={<ToolOutlined />}
            style={{ backgroundColor: themeStyle.btnBgRound }}
            onClick={handleChangePassword}
            loading={isLoading}
          >
            {t('user_menu.change_password')}
          </IdeyaButton>
        )}
        {!isInternalUser && (
          <IdeyaButton
            icon={<LogoutOutlined />}
            style={{ backgroundColor: themeStyle.btnBgRound }}
            onClick={() => navigate('/logout')}
          >
            {t('user_menu.logout')}
          </IdeyaButton>
        )}
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            color: themeStyle.footerColorText,
            fontSize: 12,
            zIndex: 1,
          }}
        >
          Copyright © {new Date().getFullYear()} Polymer. <br />
          All rights reserved.
        </div>
      </div>
    </>
  )
}

interface MenuNavigationButtonProps {
  icon: JSX.Element
  label: string
  onClick?: () => void
}

const MenuNavigationButton: React.FC<MenuNavigationButtonProps> = ({
  icon,
  label,
  onClick,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)

  return (
    <div
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {React.cloneElement(icon, {
        style: {
          color: themeStyle.naviDropdownIcon,
          fontSize: '20px',
          marginRight: themeStyle.marginXS,
        },
      })}
      <IdeyaTypography.Text size={'lg'}>{label}</IdeyaTypography.Text>
    </div>
  )
}

const SegmentLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div
      style={{
        display: 'flex',
        flex: 1,
        padding: '0px 12px',
        gap: '4px',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {children}
    </div>
  )
}
